import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Table,
  Form,
  Select,
  Modal,
  message,
  Tooltip,
  Tag,
  Icon,
  Input,
  Radio,
  Row,
  Col,
  Switch,
  Badge,
  DatePicker,
  Upload,
  Divider,
  Checkbox
} from 'antd';
import moment from 'moment';
import {
  constructColumnFilterCheckbox,
  constructColumnFilterRadio,
  constructColumnFilterSearch,
  constructColumn,
  handleOnAggregationTableChange
} from 'utils/table/table';
import { linkProfileForm, getPlanList, updatePendingOrder, validateCheckoutVoucher } from 'utils/apis/eInvoice';
import { getHosts } from '../../../utils/apis/host';
import { StyledCard, StyledHeader } from '../eInvoice.styles';
import { withAppContext } from 'context/AppContext';
import { CSVLink } from 'react-csv';
import { getConstants } from 'utils/apis/constants';
import { msicCodes } from 'utils/constants';

const FormItem = Form.Item;
const { Option, OptGroup } = Select;
const { RangePicker } = DatePicker;

const buildIconFileList = icon => {
  if (!icon || !icon.url) {
    return [];
  }

  return [
    {
      uid: icon.uid || '-1',
      name: icon.name || 'icon.png',
      status: 'done',
      url: icon.url
    }
  ];
};

const normalizeOptionalField = value => {
  if (value === null || value === undefined || value === 'null' || value === 'undefined') {
    return '';
  }
  return value;
};

const getRegularPlanOptions = plans => plans.filter(plan => !plan.name.includes('(Promo)'));

const formatVoucherDiscount = voucher => {
  if (voucher.percentageOff) {
    return `${voucher.percentageOff}% off`;
  }

  if (voucher.amountOff) {
    const currency = (voucher.currency || 'MYR').toUpperCase();
    return `${currency} ${voucher.amountOff} off`;
  }

  return 'discount applied';
};

const LinkProfileModal = props => {
  const { getFieldDecorator, getFieldValue } = props.form;
  const [loading, setLoading] = useState(false);

  const [planOptions, setPlanOptions] = useState([]);
  const [allPlanOptions, setAllPlanOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);

  const [iconPhoto, setIconPhoto] = useState([]);
  const hasPrefilledRef = useRef(false);
  const [appliedVoucherCode, setAppliedVoucherCode] = useState('');
  const [voucherApplyStatus, setVoucherApplyStatus] = useState('');
  const [voucherApplyMessage, setVoucherApplyMessage] = useState('');
  const [voucherApplying, setVoucherApplying] = useState(false);

  const resetVoucherApplyState = useCallback(() => {
    setAppliedVoucherCode('');
    setVoucherApplyStatus('');
    setVoucherApplyMessage('');
    setVoucherApplying(false);
  }, []);

  const applyVoucherCode = useCallback(
    async (code, priceId) => {
      const trimmed = (code || '').trim();

      if (!trimmed) {
        resetVoucherApplyState();
        setVoucherApplyStatus('invalid');
        setVoucherApplyMessage('Enter a voucher code to apply.');
        return false;
      }

      setVoucherApplying(true);
      const res = await validateCheckoutVoucher(trimmed, priceId);
      setVoucherApplying(false);

      if (res.status >= 200 && res.status < 300 && res.data && res.data.valid) {
        const resolvedCode = res.data.code || trimmed;
        setAppliedVoucherCode(resolvedCode);
        setVoucherApplyStatus('valid');
        setVoucherApplyMessage(`Voucher "${res.data.name || resolvedCode}" applied (${formatVoucherDiscount(res.data)}).`);
        props.form.setFieldsValue({ voucherCode: resolvedCode });
        return true;
      }

      setAppliedVoucherCode('');
      setVoucherApplyStatus('invalid');
      setVoucherApplyMessage((res && res.data && res.data.message) || 'Invalid voucher code.');
      return false;
    },
    [props.form, resetVoucherApplyState]
  );

  const resolveSelectedPriceId = useCallback(() => {
    const selectedPlan = props.form.getFieldValue('plan');
    const useAnnual = props.form.getFieldValue('annualSubscription') === true;

    if (!selectedPlan || !planOptions.length) {
      return null;
    }

    const matchedPlan = planOptions.find(plan => plan.name === selectedPlan);

    if (!matchedPlan) {
      return null;
    }

    return useAnnual ? matchedPlan.priceIdAnually : matchedPlan.priceIdMonthly;
  }, [planOptions, props.form]);

  const handleApplyVoucher = useCallback(() => {
    applyVoucherCode(props.form.getFieldValue('voucherCode'), resolveSelectedPriceId());
  }, [applyVoucherCode, props.form, resolveSelectedPriceId]);

  const handleVoucherInputChange = useCallback(
    e => {
      const nextValue = e.target.value;
      if (appliedVoucherCode && nextValue !== appliedVoucherCode) {
        setAppliedVoucherCode('');
        setVoucherApplyStatus('');
        setVoucherApplyMessage('');
      }
    },
    [appliedVoucherCode]
  );

  const handleClearVoucher = useCallback(() => {
    resetVoucherApplyState();
    props.form.setFieldsValue({ voucherCode: '' });
  }, [props.form, resetVoucherApplyState]);

  useEffect(() => {
    getConstants('countries').then(resCountries => {
      console.log('resCountries', resCountries);
      if (resCountries && resCountries.status === 200) {
        let countries = Object.keys(resCountries.data).map(k => {
          return resCountries.data[k];
        });
        if (countries.length > 0) {
          setCountryOptions(
            countries.map(u => {
              return { value: u.iso3, label: u.name };
            })
          );
        }
      } else {
        message.error('Error while retrieving countries');
      }
    });

    getConstants('statesMY').then(resStates => {
      console.log('resStates', resStates);
      if (resStates && resStates.status === 200) {
        let states = Object.keys(resStates.data).map(k => {
          return resStates.data[k];
        });
        if (states.length > 0) {
          setStateOptions(
            states.map(state => {
              return { value: state.code, label: state.label };
            })
          );
        }
      } else {
        message.error('Error while retrieving states');
      }
    });
  }, []);

  useEffect(() => {
    getPlanList().then(res => {
      console.log('getPlanList', res);
      if (res.status >= 200 && res.status < 300) {
        setAllPlanOptions(res.data.subscriptions);
        setPlanOptions(getRegularPlanOptions(res.data.subscriptions));
      }
    });
  }, []);

  useEffect(() => {
    if (!props.visible) {
      hasPrefilledRef.current = false;
      return;
    }

    if (!props.initialValues || !allPlanOptions.length || hasPrefilledRef.current) {
      return;
    }

    hasPrefilledRef.current = true;

    const values = props.initialValues;
    const iconFileList = buildIconFileList(values.icon);
    setPlanOptions(getRegularPlanOptions(allPlanOptions));
    setIconPhoto(iconFileList);

    props.form.setFieldsValue({
      voucherCode: normalizeOptionalField(values.voucherCode),
      name: values.name,
      plan: values.plan,
      amount: values.amount,
      companyName: values.companyName,
      contact: values.contact,
      email: values.email,
      brn: values.brn,
      tinNumber: values.tinNumber,
      tourismTaxRegistrationNumber: normalizeOptionalField(values.tourismTaxRegistrationNumber),
      sstRegistrationNumber: normalizeOptionalField(values.sstRegistrationNumber),
      msicCode: values.msicCode,
      businessActivityDescription: values.businessActivityDescription,
      country: values.country,
      state: values.state,
      city: values.city,
      postalCode: values.postalCode,
      companyAddress1: values.companyAddress1,
      companyAddress2: normalizeOptionalField(values.companyAddress2),
      companyAddress3: normalizeOptionalField(values.companyAddress3),
      icon: iconFileList,
      annualSubscription: false,
      termsAgreement: true
    });

    if (values.voucherCode) {
      applyVoucherCode(values.voucherCode, values.priceId);
    }
  }, [props.visible, props.initialValues, allPlanOptions, applyVoucherCode]);

  const handleOnFormSubmit = e => {
    e.preventDefault();

    props.form.validateFields((err, values) => {
      if (!err) {
        delete values.termsAgreement;
        delete values.voucherCode;
        values.paymentMethod = '04';
        values.priceId =
          values.annualSubscription === true
            ? planOptions.find(p => p.name === values.plan).priceIdAnually
            : planOptions.find(p => p.name === values.plan).priceIdMonthly;
        values.defaultCurrency = 'MYR';
        values.trial = values.plan === 'Trial Plan' ? true : false;

        delete values.annualSubscription;

        console.log('values', values);

        const formData = new FormData();

        // Remove undefined/null values and normalize optional strings
        Object.keys(values).forEach(key => {
          if (values[key] === undefined || values[key] === null) {
            delete values[key];
            return;
          }

          if (typeof values[key] === 'string') {
            values[key] = normalizeOptionalField(values[key]);
          }
        });

        // Append all form values to FormData
        Object.keys(values).forEach(key => {
          if (key === 'icon') {
            if (values[key] && values[key][0] && values[key][0].originFileObj) {
              formData.append(key, values[key][0].originFileObj);
            }
          } else if (values[key] !== '') {
            formData.append(key, values[key]);
          }
        });

        if (appliedVoucherCode) {
          formData.append('voucherCode', appliedVoucherCode);
        }

        const submitPendingOrder = props.pendingOrderId ? updatePendingOrder.bind(null, props.pendingOrderId) : linkProfileForm;

        Modal.confirm({
          title: props.pendingOrderId ? 'Confirm update signup?' : 'Confirm Link Company Profile?',
          content: 'Please make sure all the information is correct before submitting.',
          onOk() {
            setLoading(true);
            submitPendingOrder(formData).then(response => {
              setLoading(false);

              console.log('linkProfileForm', response);

              if (response.status >= 200 && response.status < 300) {
                window.location.href = response.data.redirectUrl;
                props.onCancel();
                if (props.onAfterClose) {
                  props.onAfterClose();
                }
              } else {
                message.error(response.data.message || 'Failed to link company profile');
                setLoading(false);
              }
            });

            // .catch(error => {
            //   message.error('Failed to link company profile');
            //   setLoading(false);
            // });
          }
        });
      }
    });
  };

  const normFile = e => {
    if (Array.isArray(e)) {
      return e;
    }
    if (e && e.fileList) {
      return e.fileList;
    }
  };

  return (
    <Modal
      title={props.pendingOrderId ? 'Update signup' : 'Link Company Profile'}
      width={window.innerWidth < 1200 ? '90%' : '60%'}
      visible={props.visible}
      maskClosable={false}
      afterClose={() => {
        hasPrefilledRef.current = false;
        props.form.resetFields();
        setIconPhoto([]);
        resetVoucherApplyState();
        setPlanOptions(getRegularPlanOptions(allPlanOptions));
        if (props.onAfterClose) {
          props.onAfterClose();
        }
      }}
      onCancel={() => {
        props.onCancel();
      }}
      footer={
        <div>
          <Row type="flex" justify="end">
            <Col>
              <Button
                key="back"
                onClick={() => {
                  props.onCancel();
                }}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button key="submit" onClick={handleOnFormSubmit} type="primary" loading={loading}>
                Link Company Profile
              </Button>
            </Col>
          </Row>
        </div>
      }
    >
      <Form
        // hideRequiredMark
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 14 }}
      >
        {/* Voucher can only be entered at Stripe checkout
        <Divider>Voucher Information</Divider>

        <Form.Item
          label="Voucher Code"
          help={
            getFieldValue('voucherCode') === 'HPEINVEB'
              ? '🎉 Valid voucher code! Promotional plans (Lite Promo & Pro Promo) are now available.'
              : getFieldValue('voucherCode') && getFieldValue('voucherCode') !== 'HPEINVEB'
              ? '❌ Invalid voucher code. Regular plans (Lite & Pro) will be shown.'
              : '💡 Enter voucher code "VIPAXVD" to unlock promotional plans'
          }
          validateStatus={
            getFieldValue('voucherCode') === 'HPEINVEB'
              ? 'success'
              : getFieldValue('voucherCode') && getFieldValue('voucherCode') !== 'HPEINVEB'
              ? 'error'
              : ''
          }
        >
          {getFieldDecorator('voucherCode', {
            // rules: [{ required: true, message: 'Voucher code is required' }]
          })(
            <Input
              disabled={loading}
              onChange={handleVoucherCodeChange}
              placeholder="Enter voucher code (e.g., VIPAXVD)"
              suffix={
                getFieldValue('voucherCode') === 'HPEINVEB' ? (
                  <Icon type="check-circle" style={{ color: '#52c41a' }} />
                ) : getFieldValue('voucherCode') && getFieldValue('voucherCode') !== 'HPEINVEB' ? (
                  <Icon type="close-circle" style={{ color: '#ff4d4f' }} />
                ) : null
              }
            />
            // <Select disabled={loading}>
            //   {voucherCodeOptions.map(option => (
            //     <Option key={option.code} value={option.code}>
            //       {option.name} ({option.percentageOff}% off)
            //     </Option>
            //   ))}
            // </Select>
          )}
        </Form.Item>
        */}
        <Divider>Company Profile Information</Divider>

        <Form.Item label="Name">
          {getFieldDecorator('name', {
            rules: [{ required: true, message: 'Name is required' }]
          })(<Input disabled={loading} />)}
        </Form.Item>

        <Form.Item label="Plan" help="Regular plans available">
          {getFieldDecorator('plan', {
            rules: [{ required: true, message: 'Plan is required' }]
          })(
            <Select
              disabled={loading}
              onChange={value => {
                props.form.setFieldsValue({
                  amount: planOptions.find(p => p.name === value).pricePerMonth,
                  annualSubscription: false
                });
              }}
            >
              {planOptions.map(option => (
                <Option key={option.name} value={option.name}>
                  {option.name}
                </Option>
              ))}
            </Select>
          )}
        </Form.Item>

        {/* <Form.Item label="Annual Subscription">
          {getFieldDecorator(
            'annualSubscription',
            { valuePropName: 'checked' },
            {
              // rules: [{ required: true, message: 'Annual subscription is required' }]
            }
          )(
            <Switch
              disabled={loading || !getFieldValue('plan') || getFieldValue('plan') === 'Trial Plan'}
              onChange={value => {
                if (value) {
                  props.form.setFieldsValue({
                    amount: planOptions.find(p => p.name === getFieldValue('plan')).pricePerYear
                  });
                } else {
                  props.form.setFieldsValue({
                    amount: planOptions.find(p => p.name === getFieldValue('plan')).pricePerMonth
                  });
                }
              }}
            />
          )}
        </Form.Item> */}

        <Form.Item label="Amount (RM)">
          {getFieldDecorator('amount', {
            rules: [{ required: true, message: 'Amount is required' }]
          })(<Input type="number" readOnly />)}
        </Form.Item>

        <Form.Item label="Company Name">
          {getFieldDecorator('companyName', {
            rules: [{ required: true, message: 'Company name is required' }]
          })(<Input disabled={loading} />)}
        </Form.Item>

        <Form.Item label="Contact">
          {getFieldDecorator('contact', {
            rules: [
              {
                required: true,
                message: 'Contact is required'
              },
              {
                max: 12,
                message: 'Contact No. must not more than 12 digit'
              },
              {
                pattern: /^[0-9]+$/,
                message: 'Contact No. must be number only'
              }
            ]
          })(<Input disabled={loading} />)}
        </Form.Item>

        <Form.Item label="Email">
          {getFieldDecorator('email', {
            rules: [{ required: true, message: 'Email is required' }, { type: 'email', message: 'Please enter a valid email' }]
          })(<Input disabled={loading} />)}
        </Form.Item>

        <Form.Item label="BRN">
          {getFieldDecorator('brn', {
            rules: [{ required: true, message: 'BRN is required' }]
          })(<Input disabled={loading} />)}
        </Form.Item>

        <Form.Item label="TIN Number">
          {getFieldDecorator('tinNumber', {
            rules: [{ required: true, message: 'TIN number is required' }]
          })(<Input disabled={loading} />)}
        </Form.Item>

        <Form.Item label="Tourism Tax Registration Number">
          {getFieldDecorator('tourismTaxRegistrationNumber', {
            // rules: [{ required: true, message: 'Tourism tax registration number is required' }]
          })(<Input disabled={loading} />)}
        </Form.Item>

        <Form.Item label="SST Registration Number">
          {getFieldDecorator('sstRegistrationNumber', {
            // rules: [{ required: true, message: 'SST registration number is required' }]
          })(<Input disabled={loading} />)}
        </Form.Item>

        <Form.Item label="MSIC Code">
          {getFieldDecorator('msicCode', {
            rules: [{ required: true, message: 'MSIC code is required' }]
          })(
            <Select
              showSearch
              filterOption={(input, option) => {
                if (!input) return true;
                const regex = new RegExp(input, 'i');
                return regex.test(option.props.children);
              }}
              disabled={loading}
            >
              {msicCodes.map(option => (
                <Option key={option.code} value={option.code}>
                  {option.code + ' - ' + option.label}
                </Option>
              ))}
            </Select>
          )}
        </Form.Item>

        <Form.Item label="Business Activity Description">
          {getFieldDecorator('businessActivityDescription', {
            rules: [
              { required: true, message: 'Business activity description is required' },
              { max: 300, message: 'Business activity description maximum length is 300' }
            ]
          })(<Input.TextArea disabled={loading} autoSize={{ minRows: 3, maxRows: 3 }} maxLength={300} />)}
          <div
            style={{
              textAlign: 'right',
              fontSize: 12,
              color: (getFieldValue('businessActivityDescription') || '').length >= 300 ? '#ff4d4f' : 'rgba(0, 0, 0, 0.45)'
            }}
          >
            {(getFieldValue('businessActivityDescription') || '').length} / 300
          </div>
        </Form.Item>

        <Form.Item label="Icon" help="PNG, JPG, JPEG, Max size 1MB (512 x 512)">
          {getFieldDecorator('icon', {
            valuePropName: 'fileList',
            getValueFromEvent: normFile,
            rules: [
              {
                required: !props.pendingOrderId || !(props.initialValues && props.initialValues.icon && props.initialValues.icon.url),
                message: 'Icon is required'
              }
            ]
          })(
            <Upload accept=".png,.jpg,.jpeg" maxCount={1} beforeUpload={() => false} disabled={loading} onChange={e => setIconPhoto(e.fileList)}>
              {iconPhoto.length < 1 ? (
                <Button>
                  <Icon type="plus-circle" /> Upload Icon
                </Button>
              ) : null}
            </Upload>
          )}
        </Form.Item>

        <Divider>Address Information</Divider>

        <Form.Item label="Country">
          {getFieldDecorator('country', {
            rules: [{ required: true, message: 'Country is required' }]
          })(
            <Select
              showSearch
              filterOption={(input, option) => {
                if (!input) return true;
                const regex = new RegExp(input, 'i');
                return regex.test(option.props.children);
              }}
              disabled={loading}
              onChange={value => {
                props.form.setFieldsValue({
                  state: null
                });
              }}
            >
              {countryOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          )}
        </Form.Item>

        <Form.Item label="State">
          {getFieldDecorator('state', {
            rules: [{ required: true, message: 'State is required' }]
          })(
            getFieldValue('country') === 'MYS' ? (
              <Select disabled={loading}>
                {stateOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            ) : (
              <Input disabled={loading} />
            )
          )}
        </Form.Item>

        <Form.Item label="City">
          {getFieldDecorator('city', {
            rules: [{ required: true, message: 'City is required' }]
          })(<Input disabled={loading} />)}
        </Form.Item>

        <Form.Item label="Postal Code">
          {getFieldDecorator('postalCode', {
            rules: [{ required: true, message: 'Postal code is required' }]
          })(<Input disabled={loading} />)}
        </Form.Item>

        <Form.Item label="Company Address 1">
          {getFieldDecorator('companyAddress1', {
            rules: [{ required: true, message: 'Company address 1 is required' }]
          })(<Input disabled={loading} />)}
        </Form.Item>

        <Form.Item label="Company Address 2">
          {getFieldDecorator('companyAddress2', {
            // rules: [{ required: true, message: 'Company address 2 is required' }]
          })(<Input disabled={loading} />)}
        </Form.Item>

        <Form.Item label="Company Address 3">
          {getFieldDecorator('companyAddress3', {
            // rules: [{ required: true, message: 'Company address 3 is required' }]
          })(<Input disabled={loading} />)}
        </Form.Item>

        <Divider>Voucher Information</Divider>

        <Form.Item
          label="Voucher Code"
          help={
            voucherApplyStatus === 'valid'
              ? voucherApplyMessage
              : voucherApplyStatus === 'invalid'
              ? voucherApplyMessage
              : 'Optional. Select a plan first, then apply a valid code to attach the discount to your subscription.'
          }
          validateStatus={voucherApplyStatus === 'valid' ? 'success' : voucherApplyStatus === 'invalid' ? 'error' : ''}
        >
          <Row gutter={8} type="flex" align="middle">
            <Col span={appliedVoucherCode ? 14 : 16}>
              {getFieldDecorator('voucherCode')(
                <Input
                  disabled={loading || voucherApplying}
                  placeholder="Enter voucher code"
                  onChange={handleVoucherInputChange}
                  onPressEnter={e => {
                    e.preventDefault();
                    handleApplyVoucher();
                  }}
                />
              )}
            </Col>
            <Col span={appliedVoucherCode ? 10 : 8}>
              <Button type="primary" loading={voucherApplying} disabled={loading} onClick={handleApplyVoucher} style={{ marginRight: 8 }}>
                Apply
              </Button>
              {appliedVoucherCode ? (
                <Button disabled={loading || voucherApplying} onClick={handleClearVoucher}>
                  Clear
                </Button>
              ) : null}
            </Col>
          </Row>
        </Form.Item>

        <Form.Item label=" " colon={false}>
          {getFieldDecorator('termsAgreement', {
            valuePropName: 'checked',
            rules: [
              {
                required: true,
                message: 'You must agree to the terms and conditions'
              }
            ]
          })(
            <Checkbox disabled={loading}>
              I agree to pay the agreed amount and understand it is non-refundable. I acknowledge that while I may cancel my subscription, refunds are
              not permitted.
            </Checkbox>
          )}
        </Form.Item>

        {/* <Form.Item label="eInvoice Date Range">
          {getFieldDecorator('eInvoiceDateRange', {
            rules: [{ required: true, message: 'eInvoice date range is required' }]
          })(
            <RangePicker
              disabled={loading}
              // showTime
              format="YYYY-MM-DD"
              onChange={dates => {
                if (dates) {
                  props.form.setFieldsValue({
                    eInvoiceStartDate: dates[0],
                    eInvoiceExpiryDate: dates[1]
                  });
                }
              }}
            />
          )}
        </Form.Item> */}
      </Form>
    </Modal>
  );
};

export default withAppContext(Form.create()(LinkProfileModal));
